import { PermissaoListaService } from '@/app/core/service/permissao-lista.service'
import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router, RouterModule } from '@angular/router'
import { FormsModule } from '@angular/forms'
import { NgbDropdownModule, NgbNavModule, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap'
import { SimplebarAngularModule } from 'simplebar-angular'
import { TabItems } from './data'
import { AuthenticationService } from '@/app/core/service/auth.service'
import { AuthenticatedUserService } from '@/app/core/service/authenticated-user.service'
import { ListaSelecionadaService } from '@/app/core/service/lista-selecionada.service'
import { ListaService, } from '@/app/views/lista/lista/services/lista.service'
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, merge, of, switchMap, tap, timer } from 'rxjs'
import { ListaDetailsDto } from '@/app/views/lista/lista/models/lista-details.model'
import { ThemeService } from '@/app/core/service/theme.service'
import { TaxonService } from '@/app/views/taxon/services/taxon.service'
import { TaxonBuscaRapidaDto } from '@/app/views/taxon/models/taxon.model'
import { InstituicaoService } from '@/app/views/configuracoes/instituicao/services/instituicao.service'

interface ResultadoBusca {
  id: number
  titulo: string
  subtitulo: string
  tipo: 'Fauna' | 'Flora'
}

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    NgbDropdownModule,
    SimplebarAngularModule,
    NgbNavModule,
    RouterModule,
    FormsModule,
    NgbTypeaheadModule,
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent {
  @Input() isMobileViewport = false
  @Input() isSidebarOpen = true

  tabItems = TabItems
  private themeService = inject(ThemeService)
  private router = inject(Router)
  auth = inject(AuthenticationService)
  currentUser = inject(AuthenticatedUserService)
  private listaSelecionadaService = inject(ListaSelecionadaService)
  private listaService = inject(ListaService)
  private permissaoLista = inject(PermissaoListaService)
  private taxonService = inject(TaxonService)
  private instituicaoService = inject(InstituicaoService)
  private buscaManual$ = new Subject<string>()
  scrollY = 0
  listaSelecionada: ListaDetailsDto | null = null
  private grupoListaIdAtivo: number | null = null
  termoBusca = ''
  buscando = false
  nenhumResultado = false
  erroBusca = false
  private buscaEmFoco = false

  @Output() mobileMenuButtonClicked = new EventEmitter()

  buscar = (texto$: Observable<string>) =>
    merge(texto$, this.buscaManual$).pipe(
      switchMap((termo) => {
        const termoLimpo = termo.trim()
        this.nenhumResultado = false
        this.erroBusca = false

        if (termoLimpo.length > 0 && termoLimpo.length < 2) {
          this.buscando = false
          return of([])
        }

        const grupoListaId = this.grupoListaIdAtivo
        if (grupoListaId == null) {
          this.buscando = false
          this.nenhumResultado = true
          return of([])
        }

        this.buscando = true
        return timer(250).pipe(
          switchMap(() => this.taxonService.buscarRapido(termoLimpo, grupoListaId).pipe(
            map((taxons) => taxons.map((taxon) => this.mapearTaxon(taxon))),
            catchError(() => {
              this.erroBusca = true
              return of([])
            }),
          )),
          tap((resultados) => (this.nenhumResultado = resultados.length === 0)),
          finalize(() => (this.buscando = false)),
        )
      }),
    )

  formatarResultado = (resultado: ResultadoBusca | string) =>
    typeof resultado === 'string' ? resultado : resultado.titulo

  selecionarResultado(resultado: ResultadoBusca): void {
    void this.router.navigate(['/taxon/visualizar', resultado.id])
  }

  atualizarBusca(): void {
    this.buscaEmFoco = true
    this.buscaManual$.next(this.termoBusca)
  }

  encerrarBusca(): void {
    this.buscaEmFoco = false
  }

  get mensagemBusca(): string | null {
    if (this.buscando) return 'Buscando espécies…'
    if (this.erroBusca) return 'Não foi possível realizar a busca.'
    if ((this.termoBusca.trim().length > 0 && this.termoBusca.trim().length < 2) || !this.nenhumResultado) return null
    if (this.grupoListaIdAtivo == null) return 'Grupo de trabalho ativo indisponível.'
    return 'Nenhuma espécie encontrada.'
  }

  private mapearTaxon(taxon: TaxonBuscaRapidaDto): ResultadoBusca {
    return {
      id: taxon.id,
      titulo: taxon.especie,
      subtitulo: [taxon.familia, taxon.nomePopular, taxon.listaNome].filter(Boolean).join(' · '),
      tipo: taxon.tipo,
    }
  }

  get nomeUsuario(): string {
    return this.currentUser.user()?.nome ?? 'Usuário'
  }

  get saudacao(): string {
    const hora = new Date().getHours()
    if (hora >= 5 && hora < 12) return 'Bom dia'
    if (hora >= 12 && hora < 18) return 'Boa tarde'
    return 'Boa noite'
  }

  get avatarUsuario(): string {
    return this.currentUser.user()?.avatarPath?.trim() || 'assets/users/no-photo.jpeg'
  }

  get listaSelecionadaLabel(): string {
    if (!this.listaSelecionada) return ''
    const ano = this.listaSelecionada.ano ? ` - ${this.listaSelecionada.ano}` : ''
    return `${this.listaSelecionada.nome || 'Sem nome'}${ano}`
  }

  get aviso(): string | null {
    if (!this.listaSelecionada) return null
    if (this.auth.isUsuarioExterno()) {
      return this.listaSelecionada.periodoPreenchimentoAberto ? null : 'Fora do período de preenchimento das fichas.'
    }
    return this.avisoDetalhe ? 'Fora do período da sua fase' : null
  }

  /** Fases e datas do papel na lista selecionada, mostradas ao passar o mouse no aviso. */
  get avisoDetalhe(): string | null {
    if (!this.listaSelecionada || this.auth.isUsuarioExterno()) return null
    return this.permissaoLista.bloqueioDeFase(this.listaSelecionada.id)
  }

  constructor() {
    const destroyRef = inject(DestroyRef)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    destroyRef.onDestroy(() => window.removeEventListener('scroll', this.handleScroll))
    this.handleScroll()

    this.listaSelecionadaService.listaSelecionadaId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => {
          this.listaSelecionada = null
          return id
            ? this.listaService.obterPorId(id).pipe(catchError(() => of(null)))
            : of(null)
        }),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((lista) => {
        this.listaSelecionada = lista
      })

    this.instituicaoService.obterGrupoListaIdPadrao()
      .pipe(catchError(() => of(null)), takeUntilDestroyed(destroyRef))
      .subscribe((grupoListaId) => {
        this.grupoListaIdAtivo = grupoListaId
        if (this.buscaEmFoco) this.buscaManual$.next(this.termoBusca)
      })
  }

  toggleMobileMenu() {
    this.mobileMenuButtonClicked.emit()
  }

  changeTheme() {
    this.themeService.toggle()
  }

  handleScroll = () => {
    this.scrollY = window.scrollY
  }
}
